import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Plus, Trash2, Save, Package, Users, DollarSign, Mail, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { LOGO } from "../lib/constants";

const REGIONS = ["Cancun", "Riviera Maya", "Archaeological Site", "Tourist Site"];

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="border border-[var(--border)] bg-[var(--purple)]/20 p-6">
      <Icon className="w-5 h-5 text-[var(--gold)] mb-4" />
      <div className="text-3xl font-serif">{value}</div>
      <div className="text-xs uppercase tracking-widest text-white/40 mt-1">{label}</div>
    </div>
  );
}

export default function Admin() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("bookings");
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [dests, setDests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newDest, setNewDest] = useState({ name: "", region: "Cancun", price: "", pricing_type: "per_person", duration: "", includes: "" });

  const loadAll = async () => {
    try {
      const [s, b, c, d] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/bookings"),
        api.get("/admin/contacts"),
        api.get("/destinations"),
      ]);
      setStats(s.data); setBookings(b.data); setContacts(c.data); setDests(d.data);
    } catch (e) {
      if (e.response?.status === 401) { localStorage.removeItem("hrh_token"); navigate("/admin/login"); }
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (!localStorage.getItem("hrh_token")) { navigate("/admin/login"); return; }
    loadAll();
  }, [navigate]);

  const logout = () => { localStorage.removeItem("hrh_token"); navigate("/admin/login"); };

  const updateBookingStatus = async (id, status) => {
    await api.patch(`/admin/bookings/${id}`, { status });
    setBookings((prev) => prev.map((b) => (b.id === id ? { ...b, status } : b)));
    toast.success("Updated");
  };

  const saveDest = async (d) => {
    try {
      await api.patch(`/admin/destinations/${d.id}`, { name: d.name, price: parseFloat(d.price), region: d.region, pricing_type: d.pricing_type, duration: d.duration, includes: Array.isArray(d.includes) ? d.includes : String(d.includes || "").split(",").map((s) => s.trim()).filter(Boolean) });
      toast.success("Saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const deleteDest = async (id) => {
    await api.delete(`/admin/destinations/${id}`);
    setDests((prev) => prev.filter((d) => d.id !== id));
    toast.success("Deleted");
  };

  const addDest = async () => {
    if (!newDest.name || !newDest.price) return toast.error("Name and price required");
    try {
      const { data } = await api.post("/admin/destinations", { ...newDest, price: parseFloat(newDest.price), includes: String(newDest.includes || "").split(",").map((s) => s.trim()).filter(Boolean), order: dests.length + 1 });
      setDests((prev) => [...prev, data]);
      setNewDest({ name: "", region: "Cancun", price: "", pricing_type: "per_person", duration: "", includes: "" });
      toast.success("Added");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (loading) return <div className="min-h-screen bg-[#050505] grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-[var(--gold)]" /></div>;

  return (
    <div className="min-h-screen bg-[#050505]">
      <header className="border-b border-[var(--border)] bg-black/60 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-[1500px] mx-auto px-5 md:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO} alt="HRH" className="h-10 w-10 object-contain" />
            <span className="font-serif tracking-wide">Admin <span className="text-[var(--gold)]">Panel</span></span>
          </div>
          <button onClick={logout} data-testid="admin-logout" className="flex items-center gap-2 text-xs uppercase tracking-widest text-white/60 hover:text-[var(--gold)]"><LogOut className="w-4 h-4" /> Logout</button>
        </div>
      </header>

      <div className="max-w-[1500px] mx-auto px-5 md:px-10 py-10">
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
            <Stat icon={Package} label="Total Bookings" value={stats.total_bookings} />
            <Stat icon={Users} label="Pending" value={stats.pending_bookings} />
            <Stat icon={DollarSign} label="Revenue (paid)" value={`$${stats.revenue}`} />
            <Stat icon={Mail} label="Contacts" value={stats.contacts} />
          </div>
        )}

        <div className="flex gap-2 mb-8 border-b border-[var(--border)]">
          {[["bookings", "Bookings"], ["contacts", "Contacts"], ["destinations", "Destinations & Prices"]].map(([k, label]) => (
            <button key={k} data-testid={`tab-${k}`} onClick={() => setTab(k)} className={`px-5 py-3 text-xs uppercase tracking-[0.2em] border-b-2 -mb-px transition-colors ${tab === k ? "border-[var(--gold)] text-[var(--gold)]" : "border-transparent text-white/50 hover:text-white"}`}>{label}</button>
          ))}
        </div>

        {tab === "bookings" && (
          <div className="space-y-3">
            {bookings.length === 0 && <p className="text-white/40">No bookings yet.</p>}
            {bookings.map((b) => (
              <div key={b.id} data-testid={`booking-row-${b.id}`} className="border border-[var(--border)] bg-black/30 p-5 grid md:grid-cols-[1fr_auto] gap-4">
                <div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-serif text-lg">{b.destination_name}</span>
                    <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 border border-white/20 text-white/60">{b.trip_type.replace("_", " ")}</span>
                    <span className="text-[var(--gold)]">${b.total_price} USD</span>
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 ${b.payment_status === "approved" ? "text-green-400 border border-green-400/40" : "text-white/40 border border-white/15"}`}>{b.payment_status}</span>
                  </div>
                  <div className="text-sm text-white/50 mt-2">
                    {b.customer_name} · {b.customer_email} · {b.customer_phone} · {b.passengers} pax · {b.hotel_name || "—"}
                  </div>
                  <div className="text-xs text-white/35 mt-1">
                    Arrival: {b.arrival?.airline || "—"} {b.arrival?.flight_number || ""} {b.arrival?.date || ""} {b.arrival?.time || ""}
                    {b.departure && ` | Departure: ${b.departure?.airline || "—"} ${b.departure?.flight_number || ""} ${b.departure?.date || ""} ${b.departure?.time || ""}`}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <select value={b.status} onChange={(e) => updateBookingStatus(b.id, e.target.value)} data-testid={`booking-status-${b.id}`} className="bg-transparent border border-white/20 text-xs px-3 py-2 [color-scheme:dark] outline-none">
                    {["pending", "confirmed", "completed", "cancelled"].map((s) => <option key={s} value={s} className="bg-black">{s}</option>)}
                  </select>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "contacts" && (
          <div className="space-y-3">
            {contacts.length === 0 && <p className="text-white/40">No messages yet.</p>}
            {contacts.map((c) => (
              <div key={c.id} className="border border-[var(--border)] bg-black/30 p-5">
                <div className="flex items-center gap-3"><span className="font-serif text-lg">{c.name}</span><span className="text-white/50 text-sm">{c.email}</span>{c.phone && <span className="text-white/40 text-sm">{c.phone}</span>}</div>
                <p className="text-white/60 mt-2 text-sm">{c.message}</p>
              </div>
            ))}
          </div>
        )}

        {tab === "destinations" && (
          <div>
            <div className="border border-[var(--gold)]/30 bg-[var(--purple)]/20 p-5 mb-6 grid md:grid-cols-2 lg:grid-cols-[1.3fr_0.9fr_0.9fr_0.6fr_0.8fr_1.1fr_auto] gap-3 items-end">
              <label className="text-xs text-white/50">Name<input className="w-full bg-transparent border-b border-white/20 py-2 mt-1 outline-none text-white" data-testid="new-dest-name" value={newDest.name} onChange={(e) => setNewDest({ ...newDest, name: e.target.value })} /></label>
              <label className="text-xs text-white/50">Region<select className="w-full bg-transparent border-b border-white/20 py-2 mt-1 outline-none text-white [color-scheme:dark]" value={newDest.region} onChange={(e) => setNewDest({ ...newDest, region: e.target.value })}>{REGIONS.map((r) => <option key={r} className="bg-black">{r}</option>)}</select></label>
              <label className="text-xs text-white/50">Pricing<select className="w-full bg-transparent border-b border-white/20 py-2 mt-1 outline-none text-white [color-scheme:dark]" data-testid="new-dest-pricing" value={newDest.pricing_type} onChange={(e) => setNewDest({ ...newDest, pricing_type: e.target.value })}><option value="per_person" className="bg-black">Per person</option><option value="private" className="bg-black">Private tour</option></select></label>
              <label className="text-xs text-white/50">Price $<input type="number" className="w-full bg-transparent border-b border-white/20 py-2 mt-1 outline-none text-white" data-testid="new-dest-price" value={newDest.price} onChange={(e) => setNewDest({ ...newDest, price: e.target.value })} /></label>
              <label className="text-xs text-white/50">Duration<input className="w-full bg-transparent border-b border-white/20 py-2 mt-1 outline-none text-white" value={newDest.duration} onChange={(e) => setNewDest({ ...newDest, duration: e.target.value })} /></label>
              <label className="text-xs text-white/50">Includes (comma)<input className="w-full bg-transparent border-b border-white/20 py-2 mt-1 outline-none text-white" placeholder="guide, tickets, meal" data-testid="new-dest-includes" value={newDest.includes} onChange={(e) => setNewDest({ ...newDest, includes: e.target.value })} /></label>
              <button onClick={addDest} data-testid="add-dest-button" className="btn-fill border border-[var(--gold)] text-[var(--gold)] px-5 py-2.5 text-xs uppercase tracking-widest flex items-center gap-2 transition-colors duration-300"><Plus className="w-4 h-4" /> Add</button>
            </div>

            <div className="space-y-2">
              {dests.map((d, idx) => (
                <div key={d.id} data-testid={`dest-edit-${d.id}`} className="border border-[var(--border)] bg-black/30 p-4 grid md:grid-cols-2 lg:grid-cols-[1.3fr_0.9fr_0.9fr_0.6fr_0.8fr_1.1fr_auto_auto] gap-3 items-center">
                  <input className="bg-transparent border-b border-white/15 py-2 outline-none text-white" value={d.name} onChange={(e) => setDests((p) => p.map((x, i) => i === idx ? { ...x, name: e.target.value } : x))} />
                  <select className="bg-transparent border-b border-white/15 py-2 outline-none text-white [color-scheme:dark]" value={d.region} onChange={(e) => setDests((p) => p.map((x, i) => i === idx ? { ...x, region: e.target.value } : x))}>{REGIONS.map((r) => <option key={r} className="bg-black">{r}</option>)}</select>
                  <select className="bg-transparent border-b border-white/15 py-2 outline-none text-white [color-scheme:dark]" data-testid={`dest-pricing-${d.id}`} value={d.pricing_type || "per_person"} onChange={(e) => setDests((p) => p.map((x, i) => i === idx ? { ...x, pricing_type: e.target.value } : x))}><option value="per_person" className="bg-black">Per person</option><option value="private" className="bg-black">Private tour</option></select>
                  <div className="flex items-center gap-1"><span className="text-[var(--gold)]">$</span><input type="number" className="w-full bg-transparent border-b border-white/15 py-2 outline-none text-[var(--gold)]" data-testid={`dest-price-${d.id}`} value={d.price} onChange={(e) => setDests((p) => p.map((x, i) => i === idx ? { ...x, price: e.target.value } : x))} /></div>
                  <input className="bg-transparent border-b border-white/15 py-2 outline-none text-white/70" value={d.duration || ""} onChange={(e) => setDests((p) => p.map((x, i) => i === idx ? { ...x, duration: e.target.value } : x))} />
                  <input className="bg-transparent border-b border-white/15 py-2 outline-none text-white/70" placeholder="guide, tickets, meal" data-testid={`dest-includes-${d.id}`} value={Array.isArray(d.includes) ? d.includes.join(", ") : (d.includes || "")} onChange={(e) => setDests((p) => p.map((x, i) => i === idx ? { ...x, includes: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) } : x))} />
                  <button onClick={() => saveDest(d)} data-testid={`save-dest-${d.id}`} className="text-[var(--gold)] hover:scale-110 transition-transform p-2"><Save className="w-4 h-4" /></button>
                  <button onClick={() => deleteDest(d.id)} data-testid={`delete-dest-${d.id}`} className="text-red-400/70 hover:text-red-400 p-2"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
